'use client';

import { useMemo } from 'react';
import { PortableText } from '@portabletext/react';
import { PortableTextBlock } from 'sanity';
import AutoScroll from 'embla-carousel-auto-scroll';

import { GroqImage } from '@/helpers/Image';
import { GROQButton } from '@/sanity/lib/definitions';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { IMentorItem } from '@/sanity/pages/PageMentorsTemplate';
import { Carousel, CarouselContent, CarouselItem } from '@/shadcn/ui/carousel';
import { CTAButton } from '@/shadcn/ui/button';

import { PipeChip } from '../home/_shared';
import { FadeIn } from '@/components/global/utils/FadeIn';

interface MentorsListSectionProps {
  chip?: string;
  title?: PortableTextBlock[];
  mentors?: IMentorItem[];
  cta?: GROQButton;
}

function MentorCard({ mentor }: { mentor: IMentorItem }) {
  return (
    <div className="flex flex-col gap-[3rem] w-[39.5rem] lg:w-[48.75rem]">
      <div className="overflow-hidden relative w-full rounded-[1.5rem] aspect-[390/480] bg-black/[0.04]">
        {mentor.image && (
          <GroqImage
            src={mentor.image}
            alt={mentor.name || ''}
            className="!object-cover !w-full !h-full"
          />
        )}
      </div>
      <div className="flex flex-col gap-[1.5rem]">
        <p className="m-0 leading-none font-news text-[3rem] tracking-[-0.055rem] text-black">
          {mentor.name}
        </p>
        {mentor.role && (
          <p className="m-0 leading-none font-geist text-[2rem] tracking-[0.03rem] text-black/50">
            {mentor.role}
          </p>
        )}
      </div>
    </div>
  );
}

export default function MentorsListSection({
  chip,
  title,
  mentors,
  cta,
}: MentorsListSectionProps) {
  const items = mentors ?? [];
  const autoScroll = useMemo(
    () => AutoScroll({ speed: 1, startDelay: 0, stopOnInteraction: false }),
    [],
  );
  const slides = items.length > 0 ? [...items, ...items] : [];

  return (
    <section className="overflow-hidden flex flex-col items-center justify-center w-full bg-white pt-[7.5rem] pb-[7.5rem] gap-[5rem] lg:pt-[15rem] lg:pb-[12rem] lg:gap-[7rem]">
      <div className="flex flex-col items-center text-center gap-[3.75rem] lg:gap-[5rem] w-[min(120rem,100%)] px-[2rem]">
        {chip && (
          <FadeIn delay={0}>
            <PipeChip color="black">{chip}</PipeChip>
          </FadeIn>
        )}
        {title && (
          <FadeIn
            delay={0.1}
            className="richtext prose-headings:text-black prose-p:text-black/60 [text-wrap:balance]">
            <PortableText value={title} components={richTextComponent} />
          </FadeIn>
        )}
      </div>

      {items.length > 0 && (
        <Carousel
          opts={{ align: 'start', loop: true }}
          plugins={[autoScroll]}
          className="w-full"
        >
          <CarouselContent className="ml-0">
            {slides.map((mentor, i) => (
              <CarouselItem
                key={i}
                className="basis-auto !pl-[2.5rem] lg:!pl-[2rem]"
              >
                <MentorCard mentor={mentor} />
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      )}

      {cta && (
        <div className="w-full lg:w-auto px-[2rem] lg:px-0">
          <CTAButton data={cta} fullWidth />
        </div>
      )}
    </section>
  );
}
